import { useState, useRef, useCallback, useEffect } from 'react';

interface UseVoiceDictationOptions {
  onTranscriptionComplete?: (text: string) => void;
  lang?: string;
}

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as any;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export const useVoiceDictation = (options: UseVoiceDictationOptions = {}) => {
  const { onTranscriptionComplete, lang = 'ru-RU' } = options;
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const finalTextRef = useRef('');

  const stopDictation = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  const cancelDictation = useCallback(() => {
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    finalTextRef.current = '';
    setIsRecording(false);
    setIsTranscribing(false);
  }, []);

  const startDictation = useCallback(async () => {
    setError(null);
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Speech recognition is not supported in this browser');
      return;
    }

    finalTextRef.current = '';
    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event: any) => {
      let interim = '';
      let finalChunk = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalChunk += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (finalChunk) {
        finalTextRef.current += (finalTextRef.current ? ' ' : '') + finalChunk.trim();
      }
      void interim;
    };

    recognition.onerror = (event: any) => {
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        setError(event.error || 'Dictation error');
      }
      setIsRecording(false);
      setIsTranscribing(false);
    };

    recognition.onend = () => {
      setIsRecording(false);
      setIsTranscribing(false);
      const text = finalTextRef.current.trim();
      if (text) {
        onTranscriptionComplete?.(text);
      }
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setIsRecording(true);
    setIsTranscribing(true);
    recognition.start();
  }, [lang, onTranscriptionComplete]);

  useEffect(() => () => cancelDictation(), [cancelDictation]);

  return {
    isRecording,
    isTranscribing,
    startDictation,
    stopDictation,
    cancelDictation,
    error,
  };
};
